import type { DocNode as ADF } from '@atlaskit/adf-schema';
import type { Slice, Node as PMNode } from '@atlaskit/editor-prosemirror/model';

export type LinkType = 'slack' | 'microsoft' | 'gmail' | 'other';

export interface DetectedLink {
	url: string;
	type: LinkType;
}

/**
 * Patterns for detecting integration links
 */
const LINK_PATTERNS: Record<Exclude<LinkType, 'other'>, RegExp> = {
	slack: /https?:\/\/[a-z0-9-]+\.slack\.com\/.*/i,
	microsoft: /https?:\/\/(teams\.microsoft\.com|outlook\.office\.com|outlook\.live\.com)\/.*/i,
	gmail: /https?:\/\/mail\.google\.com\/.*/i,
};

/**
 * Type guard to check if a node has attrs
 */
const hasAttrs = (node: any): node is { attrs: Record<string, any> } => {
	return node && typeof node === 'object' && 'attrs' in node;
};

/**
 * Type guard to check if a node has marks
 */
const hasMarks = (node: any): node is { marks: Array<any> } => {
	return node && typeof node === 'object' && 'marks' in node && Array.isArray(node.marks);
};

/**
 * Extract all links from ADF content iteratively using a queue
 * Uses 'any' type for node parameter because ADF child nodes have different types
 * than DocNode (text, inlineCard, blockCard, etc.)
 */
const extractLinksFromAdf = (rootNode: any): string[] => {
	if (!rootNode || typeof rootNode !== 'object') {
		return [];
	}

	const links: string[] = [];
	const queue: any[] = [rootNode];

	while (queue.length > 0) {
		const node = queue.shift();

		if (!node || typeof node !== 'object') {
			continue;
		}

		// Check if current node is a text node with link marks
		if (node.type === 'text' && hasMarks(node)) {
			const linkMark = node.marks.find((mark: any) => mark.type === 'link' && mark.attrs?.href);
			if (linkMark) {
				links.push(linkMark.attrs.href);
			}
		}

		// Check for smart card nodes (inlineCard, blockCard, embedCard)
		if (node.type === 'inlineCard' || node.type === 'blockCard' || node.type === 'embedCard') {
			if (hasAttrs(node) && node.attrs.url) {
				links.push(node.attrs.url);
			}
		}

		// Add child nodes to queue
		if (node.content && Array.isArray(node.content)) {
			queue.push(...node.content);
		}
	}

	return links;
};

/**
 * Extract all links from a ProseMirror Slice (pasted content)
 * More efficient than extractLinksFromAdf as it only processes pasted content
 */
export const extractLinksFromSlice = (slice: Slice | null | undefined): string[] => {
	if (!slice || !slice.content) {
		return [];
	}

	const links: string[] = [];
	const queue: PMNode[] = [];

	// Add all top-level nodes from the slice to the queue
	slice.content.forEach((node: PMNode) => {
		queue.push(node);
	});

	// Process nodes using BFS (same logic as extractLinksFromAdf)
	while (queue.length > 0) {
		const node = queue.shift();

		if (!node) {
			continue;
		}

		// Check for text nodes with link marks
		if (node.type.name === 'text' && node.marks) {
			const linkMark = node.marks.find((mark) => mark.type.name === 'link' && mark.attrs?.href);
			if (linkMark) {
				links.push(linkMark.attrs.href);
			}
		}

		// Check for smart card nodes (inlineCard, blockCard, embedCard)
		const nodeName = node.type.name;
		if (nodeName === 'inlineCard' || nodeName === 'blockCard' || nodeName === 'embedCard') {
			if (node.attrs?.url) {
				links.push(node.attrs.url);
			}
		}

		// Add child nodes to queue
		if (node.content) {
			// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
			node.content.forEach((childNode: PMNode) => {
				queue.push(childNode);
			});
		}
	}

	return links;
};

/**
 * Determine the type of a link based on URL patterns
 */
const classifyLink = (url: string): LinkType => {
	for (const [type, pattern] of Object.entries(LINK_PATTERNS)) {
		if (pattern.test(url)) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return type as LinkType;
		}
	}
	return 'other';
};

/**
 * Convert URL strings to DetectedLink[] (integration types only).
 * Used when we have URLs from another source (e.g. pasted ProseMirror slice).
 */
export const getDetectedLinksFromUrls = (urls: string[]): DetectedLink[] => {
	const uniqueUrls = new Set(urls);
	const result: DetectedLink[] = [];

	for (const url of uniqueUrls) {
		const type = classifyLink(url);
		if (type !== 'other') {
			result.push({ url, type });
		}
	}

	return result;
};

/**
 * Detect integration links from ADF content
 */
export const detectIntegrationLinks = (adfValue: ADF | null | undefined): DetectedLink[] => {
	const allLinks = extractLinksFromAdf(adfValue);
	return getDetectedLinksFromUrls(allLinks);
};

/**
 * Get the primary link type from detected links (prioritized)
 */
export const getPrimaryLinkType = (links: DetectedLink[]): LinkType | null => {
	if (links.length === 0) {
		return null;
	}

	// Build a set of available types for O(1) lookup
	const availableTypes = new Set(links.map((link) => link.type));

	// Priority order: Slack > Microsoft > Gmail
	const priority: LinkType[] = ['slack', 'microsoft', 'gmail'];

	for (const type of priority) {
		if (availableTypes.has(type)) {
			return type;
		}
	}

	return links[0].type;
};

/**
 * Get all unique link types from detected links
 * Returns them in priority order: Slack, Microsoft, Gmail
 */
export const getUniqueLinkTypes = (links: DetectedLink[]): LinkType[] => {
	const uniqueTypes = new Set<LinkType>();

	for (const link of links) {
		if (link.type !== 'other') {
			uniqueTypes.add(link.type);
		}
	}

	// Return in priority order: Slack, Microsoft, Gmail
	const priority: LinkType[] = ['slack', 'microsoft', 'gmail'];
	return priority.filter((type) => uniqueTypes.has(type));
};

/**
 * Get newly added link types by comparing current and initial content
 * @param currentLinks - Currently detected integration links
 * @param initialLinkTypes - Link types that existed before editing
 * @returns Array of newly added link types in priority order
 */
export const getNewLinkTypes = (
	currentLinks: DetectedLink[],
	initialLinkTypes: LinkType[],
): LinkType[] => {
	const currentTypes = getUniqueLinkTypes(currentLinks);
	const initialSet = new Set(initialLinkTypes);

	// Filter to only include types that weren't in initial content
	return currentTypes.filter((type) => !initialSet.has(type));
};
